import React, { Suspense } from "react";
import { Navigate, Outlet, Route, Routes } from "react-router-dom";
import { useSelector } from "react-redux";

// Utils
import ProtectedRouting from "./ProtectedRouting";
import MainLayout from "../layouts/MainPage";
import AuthLayout from "../layouts/Authentication";
import LandingLayout from "../layouts/Landing";
import Loader from "./FallBackLoader";
import { NAV_HEIGHT } from "../layouts/Landing/Navbar";
import TypographyComponent from "../components/Base/TypographyComponent";
import { SECTION_MARGIN } from "../config/constants";
import BasicPublicScreenWithHeading from "../components/Templates/BasicPublicScreenWithHeading";

// Pages
const Login = React.lazy(() => import("../pages/login"));
const Otp = React.lazy(() => import("../pages/otp"));
const LandingPage = React.lazy(() => import("../pages/landing-page"));
const AboutUs = React.lazy(() => import("../pages/about-us"));
const Services = React.lazy(() => import("../pages/services"));
const ContactUs = React.lazy(() => import("../pages/contact-us"));
const Home = React.lazy(() => import("../pages/home"));
const Register = React.lazy(() => import("../pages/register"));
const RateAndReview = React.lazy(() => import("../pages/rate-and-review"));
const CompletedJobs = React.lazy(() => import("../pages/completed-jobs"));
const ActiveJobs = React.lazy(() => import("../pages/active-jobs"));
const EarningReport = React.lazy(() => import("../pages/earning-report"));
const Chat = React.lazy(() => import("../pages/chat"));
const Profile = React.lazy(() => import("../pages/profile"));
const NewJob = React.lazy(() => import("../pages/new-job/"));
const Removals = React.lazy(() => import("../pages/new-job/removal"));
const Deliveries = React.lazy(() => import("../pages/new-job/deliveries"));
const ManAndVan = React.lazy(() => import("../pages/new-job/man-and-van"));
const FurnitureAssembly = React.lazy(() =>
  import("../pages/new-job/furniture-assembly")
);
const Cleaning = React.lazy(() => import("../pages/new-job/cleaning"));
const ShopAndDeliver = React.lazy(() =>
  import("../pages/new-job/shop-and-deliver")
);
const CarTransport = React.lazy(() => import("../pages/new-job/car-transport"));
const MobileBarbers = React.lazy(() =>
  import("../pages/new-job/mobile-barbers")
);
const MobileNailTechnicians = React.lazy(() =>
  import("../pages/new-job/mobile-nail-technicians")
);
const MobileHairDressers = React.lazy(() =>
  import("../pages/new-job/mobile-hair-dressers")
);
const TermsAndConditions = React.lazy(() =>
  import("../pages/terms-and-conditions")
);
const PrivacyPolicy = React.lazy(() => import("../pages/privacy-policy"));
const PricingDetails = React.lazy(() => import("../pages/pricing-details"));
const PaymentMethod = React.lazy(() => import("../pages/payment-method"));

export const newJobsArray = [
  { route: "removals", component: <Removals /> },
  { route: "deliveries", component: <Deliveries /> },
  { route: "man-and-van", component: <ManAndVan /> },
  { route: "furniture-assembly", component: <FurnitureAssembly /> },
  { route: "cleaning", component: <Cleaning /> },
  { route: "shop-and-deliver", component: <ShopAndDeliver /> },
  { route: "car-transport", component: <CarTransport /> },
  { route: "mobile-barbers", component: <MobileBarbers /> },
  { route: "mobile-nail-technicians", component: <MobileNailTechnicians /> },
  { route: "mobile-hair-dressers", component: <MobileHairDressers /> },
];

const NotFoundPage = () => {
  return (
    <LandingLayout>
      <div style={{ padding: `0 ${SECTION_MARGIN}` }}>
        <TypographyComponent variant="mainPageHeading" component="h1">
          404 - Page Not Found
        </TypographyComponent>
        <TypographyComponent component="p">
          Sorry, the page you are looking for does not exist.
        </TypographyComponent>
      </div>
    </LandingLayout>
  );
};

const LazyLoadComponent = ({ component }) => {
  return <Suspense fallback={<Loader />}>{component}</Suspense>;
};

const PublicPagesWithHeading = ({ heading, component, withMargin }) => {
  return (
    <>
      <div style={{ height: NAV_HEIGHT }}>&nbsp;</div>
      <BasicPublicScreenWithHeading withMargin={withMargin} heading={heading}>
        {component}
      </BasicPublicScreenWithHeading>
    </>
  );
};

const ApplicationRoutes = () => {
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  return (
    <Routes>
      <Route
        path="/login"
        element={
          isLoggedIn ? (
            <Navigate to="/" />
          ) : (
            <AuthLayout>
              <LazyLoadComponent component={<Login />} />
            </AuthLayout>
          )
        }
      />
      <Route
        path="/otp"
        element={
          isLoggedIn ? (
            <Navigate to="/" />
          ) : (
            <AuthLayout>
              <LazyLoadComponent component={<Otp />} />
            </AuthLayout>
          )
        }
      />
      <Route
        path="/register"
        element={
          <AuthLayout>
            <LazyLoadComponent component={<Register />} />
          </AuthLayout>
        }
      />

      <Route
        path="/"
        element={
          <LandingLayout>
            <Outlet />
          </LandingLayout>
        }
      >
        <Route
          path=""
          element={<LazyLoadComponent component={<LandingPage />} />}
        />
        <Route
          path="about-us"
          element={
            <LazyLoadComponent
              component={
                <PublicPagesWithHeading
                  withMargin={false}
                  heading="About Us"
                  component={<AboutUs />}
                />
              }
            />
          }
        />
        <Route
          path="contact-us"
          element={
            <LazyLoadComponent
              component={
                <PublicPagesWithHeading
                  heading="Contact Us"
                  withMargin={false}
                  component={<ContactUs />}
                />
              }
            />
          }
        />
        <Route
          path="services"
          element={
            <LazyLoadComponent
              component={
                <PublicPagesWithHeading
                  withMargin={false}
                  heading="Our Services"
                  component={<Services />}
                />
              }
            />
          }
        />
        <Route
          path="terms-and-conditions"
          element={
            <LazyLoadComponent
              component={
                <PublicPagesWithHeading
                  heading="Terms & Conditions"
                  component={<TermsAndConditions />}
                />
              }
            />
          }
        />
        <Route
          path="privacy-policy"
          element={
            <LazyLoadComponent
              component={
                <PublicPagesWithHeading
                  heading="Privacy Policy"
                  component={<PrivacyPolicy />}
                />
              }
            />
          }
        />
      </Route>
      <Route
        path="/home"
        element={
          <ProtectedRouting
            route={
              <MainLayout>
                <LazyLoadComponent component={<Home />} />
              </MainLayout>
            }
          />
        }
      />
      <Route
        path="/pricing-details"
        element={
          <ProtectedRouting
            route={
              <MainLayout>
                <LazyLoadComponent component={<PricingDetails />} />
              </MainLayout>
            }
          />
        }
      />
      <Route
        path="/public/new-job"
        element={
          <LandingLayout>
            <Outlet />
          </LandingLayout>
        }
      >
        <Route path="" element={<LazyLoadComponent component={<NewJob />} />} />
        {newJobsArray.map((eachJobRoute) => (
          <Route
            key={eachJobRoute.route}
            path={eachJobRoute.route}
            element={<LazyLoadComponent component={eachJobRoute.component} />}
          />
        ))}
      </Route>
      <Route
        path="/new-job"
        element={
          <ProtectedRouting
            route={
              <MainLayout>
                <Outlet />
              </MainLayout>
            }
          />
        }
      >
        <Route path="" element={<LazyLoadComponent component={<NewJob />} />} />
        {newJobsArray.map((eachJobRoute) => (
          <Route
            key={eachJobRoute.route}
            path={eachJobRoute.route}
            element={<LazyLoadComponent component={eachJobRoute.component} />}
          />
        ))}
      </Route>

      <Route
        path="/rate"
        element={
          <ProtectedRouting
            route={
              <MainLayout>
                <LazyLoadComponent component={<RateAndReview />} />
              </MainLayout>
            }
          />
        }
      />
      <Route
        path="/chat"
        element={
          <ProtectedRouting
            route={
              <MainLayout>
                <LazyLoadComponent component={<Chat />} />
              </MainLayout>
            }
          />
        }
      />
      <Route
        path="/payment"
        element={
          <ProtectedRouting
            route={
              <MainLayout>
                <LazyLoadComponent component={<PaymentMethod />} />
              </MainLayout>
            }
          />
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRouting
            route={
              <MainLayout>
                <LazyLoadComponent component={<Profile />} />
              </MainLayout>
            }
          />
        }
      />

      <Route
        path="/earning-report"
        element={
          <ProtectedRouting
            route={
              <MainLayout>
                <Outlet />
              </MainLayout>
            }
          />
        }
      >
        <Route
          path=":job_id"
          element={<LazyLoadComponent component={<EarningReport />} />}
        />
        <Route
          path=""
          element={<LazyLoadComponent component={<Navigate to="/home" />} />}
        />
      </Route>
      <Route
        path="/completed-work"
        element={
          <ProtectedRouting
            route={
              <MainLayout>
                <LazyLoadComponent component={<CompletedJobs />} />
              </MainLayout>
            }
          />
        }
      />

      <Route
        path="/active-jobs"
        element={
          <ProtectedRouting
            route={
              <MainLayout>
                <LazyLoadComponent component={<ActiveJobs />} />
              </MainLayout>
            }
          />
        }
      />
      <Route
        path="/*"
        element={
          <>
            <div style={{ height: NAV_HEIGHT }}>&nbsp;</div>
            <NotFoundPage />
          </>
        }
      />
    </Routes>
  );
};

export default ApplicationRoutes;
